/**
 * Pure payload builders for Osano consent RudderStack events.
 * Kept free of React imports so each function is unit-testable in isolation.
 *
 * @shared Used by:
 *   - hooks/useOsanoConsentEvents
 */
import { sessionStorageKeys } from 'constants/common'
import { OsanoConsent } from 'lib/osanoAnalyticsConsent'

// ---------------------------------------------------------------------------
// Snapshot metadata
// ---------------------------------------------------------------------------

export type OsanoSnapshotMetadata = {
  timestamp: string
  jurisdiction?: string
  osano_mode?: string
  is_new_consent: boolean
  anonymousId?: string
}

export type OsanoConsentSnapshotPayload = {
  consent: OsanoConsent
  metadata: OsanoSnapshotMetadata
}

/**
 * Read Osano.cm.jurisdiction and Osano.cm.mode from the global.  Both are
 * optional — omit each if undefined (no empty-string pollution).
 */
function readOsanoCmMeta(): { jurisdiction?: string; osano_mode?: string } {
  const cm = window.Osano?.cm

  if (!cm) {
    return {}
  }

  const out: { jurisdiction?: string; osano_mode?: string } = {}

  if (cm.jurisdiction) {
    out.jurisdiction = cm.jurisdiction
  }

  if (cm.mode) {
    out.osano_mode = cm.mode
  }

  return out
}

/**
 * Derive the anonymousId.  Prefer the Split-set globalThis value (set after
 * analytics consent via treatment.ts); fall back to RudderStack's own id.
 * Omit if neither is available.
 */
export function getOsanoAnonymousId(): string | undefined {
  if (globalThis.anonymousId) {
    return globalThis.anonymousId as string
  }

  if (
    globalThis.rudderanalytics &&
    typeof globalThis.rudderanalytics.getAnonymousId === 'function'
  ) {
    return globalThis.rudderanalytics.getAnonymousId() as string | undefined
  }

  return undefined
}

/**
 * Build the full osano_consent_snapshot payload.
 */
export function buildConsentSnapshotPayload(
  consent: OsanoConsent,
  isNewConsent: boolean
): OsanoConsentSnapshotPayload {
  const meta: OsanoSnapshotMetadata = {
    timestamp: new Date().toISOString(),
    ...readOsanoCmMeta(),
    is_new_consent: isNewConsent,
  }

  const anonymousId = getOsanoAnonymousId()

  if (anonymousId) {
    meta.anonymousId = anonymousId
  }

  return { consent, metadata: meta }
}

// ---------------------------------------------------------------------------
// Session flag helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if the initialized-triggered snapshot has already been sent
 * this browser-tab session.
 */
export function isInitializedSnapshotSent(): boolean {
  try {
    return (
      sessionStorage.getItem(sessionStorageKeys.OSANO_CONSENT_SNAPSHOT_SENT) ===
      '1'
    )
  } catch {
    // sessionStorage unavailable (e.g. Safari private mode strict settings)
    return false
  }
}

/**
 * Mark the initialized-triggered snapshot as sent for this tab session.
 * Only call after the snapshot has actually been dispatched.
 */
export function markInitializedSnapshotSent(): void {
  try {
    sessionStorage.setItem(sessionStorageKeys.OSANO_CONSENT_SNAPSHOT_SENT, '1')
  } catch {
    // Best-effort; ignore if sessionStorage is unavailable
  }
}
